import { useRef, useState, useEffect, useImperativeHandle, type KeyboardEvent, useMemo, useCallback } from 'react';
import type { OtpInputRef, OtpInputProps } from '../molecules/otp-input/otp-input.types';

export const useOtpInput = (props: OtpInputProps, ref: React.Ref<OtpInputRef>) => {
    const {
        numberOfDigits = 6,
        autoFocus = true,
        blurOnFilled = true,
        disabled = false,
        type = 'numeric',
        onAction,
    } = props;

    const [otp, setOtp] = useState<string[]>(() => new Array(numberOfDigits).fill(''));
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    /* ---------------- Effects ---------------- */
    useEffect(() => {
        inputRefs.current = inputRefs.current.slice(0, numberOfDigits);
    }, [numberOfDigits]);

    useEffect(() => {
        if (autoFocus && !disabled) inputRefs.current[0]?.focus();
    }, [autoFocus, disabled]);

    /* ---------------- Imperative API ---------------- */
    useImperativeHandle(ref, () => ({
        clear() {
            const empty = new Array(numberOfDigits).fill('');
            setOtp(empty);
            inputRefs.current[0]?.focus();
            onAction?.('clear', '');
        },
        focus() {
            inputRefs.current[0]?.focus();
        },
        setValue(value: string) {
            const next = value.slice(0, numberOfDigits).split('');
            while (next.length < numberOfDigits) next.push('');
            setOtp(next);
            onAction?.('paste', next.join(''));
        },
    }));

    /* ---------------- Utils ---------------- */
    const inputMode = useMemo<'numeric' | 'text'>(() => {
        return type === 'numeric' ? 'numeric' : 'text';
    }, [type]);

    const isValidCharacter = useCallback(
        (char: string) => {
            /* istanbul ignore next */
            if (!char) return false;
            if (type === 'numeric') return /^\d$/.test(char);
            if (type === 'alpha') return /^[a-zA-Z]$/.test(char);
            return /^[a-zA-Z0-9]$/.test(char);
        },
        [type]
    );

    /* ---------------- Handlers ---------------- */
    const handlePaste = useCallback(
        (text: string, startIndex: number) => {
            const chars = text.split('').filter(isValidCharacter);
            const next = [...otp];

            chars.forEach((char, i) => {
                const idx = startIndex + i;
                if (idx < numberOfDigits) next[idx] = char;
            });

            setOtp(next);

            const result = next.join('');
            onAction?.('paste', result);

            const lastIndex = Math.min(startIndex + chars.length - 1, numberOfDigits - 1);

            if (lastIndex === numberOfDigits - 1) {
                if (blurOnFilled) inputRefs.current[lastIndex]?.blur();
                onAction?.('filled', result);
            } else {
                inputRefs.current[lastIndex + 1]?.focus();
            }
        },
        [otp, numberOfDigits, blurOnFilled, isValidCharacter, onAction]
    );

    const handleChange = useCallback(
        (index: number, value: string) => {
            if (disabled) return;

            if (value.length > 1) {
                handlePaste(value, index);
                return;
            }

            if (value && !isValidCharacter(value)) return;

            const next = [...otp];
            next[index] = value;
            setOtp(next);

            const result = next.join('');
            onAction?.('change', result);

            if (value && index < numberOfDigits - 1) {
                inputRefs.current[index + 1]?.focus();
            }

            if (value && index === numberOfDigits - 1) {
                if (blurOnFilled) inputRefs.current[index]?.blur();
                onAction?.('filled', result);
            }
        },
        [disabled, otp, numberOfDigits, blurOnFilled, isValidCharacter, handlePaste, onAction]
    );

    const handleKeyDown = useCallback(
        (index: number, e: KeyboardEvent<HTMLInputElement>) => {
            if (disabled) return;

            if (e.key === 'Backspace' && !otp[index] && index > 0) {
                inputRefs.current[index - 1]?.focus();
                return;
            }

            if (e.key === 'Delete') {
                const next = [...otp];
                next[index] = '';
                setOtp(next);
                onAction?.('change', next.join(''));
                return;
            }

            if (e.key === 'ArrowLeft' && index > 0) {
                e.preventDefault();
                inputRefs.current[index - 1]?.focus();
            }

            if (e.key === 'ArrowRight' && index < numberOfDigits - 1) {
                e.preventDefault();
                inputRefs.current[index + 1]?.focus();
            }
        },
        [disabled, otp, numberOfDigits, onAction]
    );

    return {
        otp,
        focusedIndex,
        inputRefs,
        inputMode,
        handleChange,
        handleKeyDown,
        handlePaste,
        setFocusedIndex,
    };
};
